import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject } from '../api/projects'
import { useAuth } from '../AuthContext'
import { apiClient } from '../api/client'
import { PermissionRow } from '../api/admin'
import { getProjectNetworks } from '../api/commNetworks'
import { getProjectInterfaces, saveInterfaceBinding, type ProjectInterfaceBinding } from '../api/projectInterfaces'
import { useToastStore } from '../store/toastStore'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { Edit2, Plus, X, Bold, Italic, Link2, List, Code, FileText, Eye, Trash2, Wifi } from 'lucide-react'
import { getProjectDevices } from '../api/deviceInstances'
import {
  getNetworkWifiClients,
  createWifiClient,
  updateWifiClient,
  deleteWifiClient,
  type WifiClient
} from '../api/wifiClients'

// Функция для автовычисления полного IP
function calculateFullIp(subnet: string | undefined, lastOctet: number | string | null): string {
  if (!subnet) return ''
  if (lastOctet === null || lastOctet === undefined || lastOctet === '') return ''
  
  // Убираем CIDR маску, если она есть (например, 10.70.66.0/24 -> 10.70.66.0)
  const cleanSubnet = subnet.split('/')[0].trim()
  const parts = cleanSubnet.split('.')
  if (parts.length >= 3) {
    return `${parts[0]}.${parts[1]}.${parts[2]}.${lastOctet}`
  }
  return ''
}

export default function ProjectInterfacesPage() {
  const { projectId, networkId } = useParams<{ projectId: string, networkId: string }>()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()
  const { show: showToast } = useToastStore()

  // Состояния для примечаний
  const [editingNoteItem, setEditingNoteItem] = useState<ProjectInterfaceBinding | WifiClient | null>(null)
  const [editingNoteValue, setEditingNoteValue] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write')
  const [activeTooltip, setActiveTooltip] = useState<{
    note: string
    x: number
    y: number
    showAbove: boolean
  } | null>(null)

  // Локальные буферы ввода для текстовых/числовых полей
  const [localValues, setLocalValues] = useState<Record<string, string>>({})
  const [localMacs, setLocalMacs] = useState<Record<string, string>>({})

  // Состояния для WiFi-клиентов
  const [isWifiModalOpen, setIsWifiModalOpen] = useState(false)
  const [editingWifiClient, setEditingWifiClient] = useState<WifiClient | null>(null)
  const [wifiClientName, setWifiClientName] = useState('')
  const [wifiDeviceInstanceId, setWifiDeviceInstanceId] = useState<string>('')
  const [wifiIpLastOctet, setWifiIpLastOctet] = useState<string>('')
  const [wifiMacAddress, setWifiMacAddress] = useState('')
  const [wifiNote, setWifiNote] = useState('')
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('')

  // Загрузка проекта
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  // Загрузка сетей проекта
  const { data: networks = [], isLoading: networksLoading } = useQuery({
    queryKey: ['projectNetworks', projectId],
    queryFn: () => getProjectNetworks(Number(projectId!)),
    enabled: !!projectId
  })

  // Загрузка интерфейсов проекта
  const { data: interfaces = [], isLoading: interfacesLoading } = useQuery({
    queryKey: ['projectInterfaces', projectId],
    queryFn: () => getProjectInterfaces(Number(projectId!)),
    enabled: !!projectId
  })

  // Определение текущей выбранной сети
  const currentNetwork = useMemo(() => {
    return networks.find(n => n.id === Number(networkId))
  }, [networks, networkId])

  // Загрузка WiFi-клиентов
  const { data: wifiClients = [], isLoading: wifiClientsLoading } = useQuery({
    queryKey: ['networkWifiClients', networkId],
    queryFn: () => getNetworkWifiClients(Number(networkId!)),
    enabled: !!networkId && !!currentNetwork && currentNetwork.network_type === 'lan'
  })

  // Загрузка оборудования проекта
  const { data: projectDevices = [] } = useQuery({
    queryKey: ['projectDevices', projectId],
    queryFn: () => getProjectDevices(Number(projectId!), undefined, 'device'),
    enabled: !!projectId
  })

  // Фильтрация приборов для live-поиска
  const filteredProjectDevices = useMemo(() => {
    if (!deviceSearchQuery.trim()) return projectDevices
    const query = deviceSearchQuery.toLowerCase()
    return projectDevices.filter(dev => {
      const label = (dev.position_label || '').toLowerCase()
      const manuf = (dev.manufacturer || '').toLowerCase()
      const model = (dev.model || '').toLowerCase()
      const specName = (dev.spec_name || '').toLowerCase()
      return label.includes(query) || manuf.includes(query) || model.includes(query) || specName.includes(query)
    })
  }, [projectDevices, deviceSearchQuery])

  // Загрузка прав доступа к проекту
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  const { data: permissionsData } = useQuery({
    queryKey: ['my-permissions', currentUser?.id, projectId],
    queryFn: async () => {
      if (!currentUser?.id || !projectId) return { permissions: [] }
      const response = await apiClient.get('/api/auth/my-permissions', {
        params: { project_id: Number(projectId) },
      })
      return response.data as { permissions: PermissionRow[] }
    },
    enabled: !!currentUser?.id && !!projectId && currentUser.role !== 'system_admin',
  })

  const allPermissions: PermissionRow[] = permissionsData?.permissions ?? []

  // Проверка прав на редактирование
  const canEdit = useMemo(() => {
    if (currentUser?.role === 'system_admin') return true
    if (!projectId || !currentUser?.id) return false

    const pagePermission = allPermissions.find(
      (p) =>
        p.project_id === Number(projectId) &&
        (p.resource_type === 'page:devices' || p.resource_type === 'page:device-instances') &&
        p.resource_id === null &&
        p.permission !== 'deny' &&
        (p.permission === 'edit' || p.permission === 'manage')
    )
    if (pagePermission) return true

    const access = projectAccessData?.access?.find(
      (a) => a.project_id === Number(projectId),
    )
    if (access && access.role_in_project) {
      return ['editor', 'project_admin'].includes(access.role_in_project)
    }

    return false
  }, [currentUser, projectId, allPermissions, projectAccessData])

  // Фильтрация сетей под конкретный порт (для выпадающего списка)
  const getFilteredNetworks = (interfaceTypeCode: string) => {
    return networks.filter(network => {
      if (interfaceTypeCode === 'lan') {
        return network.network_type === 'lan'
      }
      if (interfaceTypeCode === 'rs485') {
        return network.network_type === 'rs485' || network.network_type === 'rs485_modbus'
      }
      return network.network_type === interfaceTypeCode
    })
  }

  // Мутация сохранения привязки физического интерфейса
  const saveMutation = useMutation({
    mutationFn: saveInterfaceBinding,
    onSuccess: () => {
      showToast('Изменения успешно сохранены')
      queryClient.invalidateQueries({ queryKey: ['projectInterfaces', projectId] })
    },
    onError: (err: any) => {
      console.error(err)
      showToast('Ошибка при сохранении: ' + (err?.response?.data?.error || err.message), 'error')
    }
  })

  // Мутация создания WiFi клиента
  const createWifiClientMutation = useMutation({
    mutationFn: createWifiClient,
    onSuccess: () => {
      showToast('Беспроводной клиент успешно добавлен')
      queryClient.invalidateQueries({ queryKey: ['networkWifiClients', networkId] })
    },
    onError: (err: any) => {
      console.error(err)
      showToast('Ошибка при добавлении: ' + (err?.response?.data?.error || err.message), 'error')
    }
  })

  // Мутация обновления WiFi клиента
  const updateWifiClientMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Omit<Partial<WifiClient>, 'id' | 'network_id'> }) =>
      updateWifiClient(id, data),
    onSuccess: () => {
      showToast('Данные беспроводного клиента обновлены')
      queryClient.invalidateQueries({ queryKey: ['networkWifiClients', networkId] })
      queryClient.invalidateQueries({ queryKey: ['projectInterfaces', projectId] })
    },
    onError: (err: any) => {
      console.error(err)
      showToast('Ошибка при сохранении: ' + (err?.response?.data?.error || err.message), 'error')
    }
  })

  // Мутация удаления WiFi клиента
  const deleteWifiClientMutation = useMutation({
    mutationFn: deleteWifiClient,
    onSuccess: () => {
      showToast('Беспроводной клиент удален')
      queryClient.invalidateQueries({ queryKey: ['networkWifiClients', networkId] })
    },
    onError: (err: any) => {
      console.error(err)
      showToast('Ошибка при удалении: ' + (err?.response?.data?.error || err.message), 'error')
    }
  })

  // Подсчет дубликатов IP-адресов / Slave ID
  const duplicateStatus = useMemo(() => {
    const counts = new Map<number, number>()
    if (!currentNetwork) return { isDuplicate: (_val: number | null) => false }

    const isLan = currentNetwork.network_type === 'lan'
    const isModbus = ['rs485', 'rs485_modbus'].includes(currentNetwork.network_type)

    if (isLan) {
      // Физические порты
      interfaces.forEach(item => {
        if (item.network_id === currentNetwork.id && item.ip_last_octet !== null) {
          counts.set(item.ip_last_octet, (counts.get(item.ip_last_octet) || 0) + 1)
        }
      })
      // WiFi клиенты
      wifiClients.forEach(client => {
        if (client.ip_last_octet !== null) {
          counts.set(client.ip_last_octet, (counts.get(client.ip_last_octet) || 0) + 1)
        }
      })
    } else if (isModbus) {
      interfaces.forEach(item => {
        if (item.network_id === currentNetwork.id && item.slave_id !== null) {
          counts.set(item.slave_id, (counts.get(item.slave_id) || 0) + 1)
        }
      })
    }

    return {
      isDuplicate: (val: number | null) => {
        if (val === null) return false
        return (counts.get(val) || 0) > 1
      }
    }
  }, [currentNetwork, interfaces, wifiClients])

  // Фильтрация интерфейсов под совместимый тип с текущей сетью
  const filteredInterfaces = useMemo(() => {
    if (!currentNetwork) return []
    const netType = currentNetwork.network_type
    
    return interfaces.filter(item => {
      const ifaceTypeCode = item.interface_type_code
      
      // LAN-сети показывают только LAN-порты устройств
      if (netType === 'lan') {
        return ifaceTypeCode === 'lan'
      }
      // RS-485/Modbus-сети показывают только RS-485 порты устройств
      if (netType === 'rs485' || netType === 'rs485_modbus') {
        return ifaceTypeCode === 'rs485'
      }
      
      return ifaceTypeCode === netType
    })
  }, [interfaces, currentNetwork])

  // Функция для очистки разметки в таблице
  const getNotePreview = (note: string | null): string => {
    if (!note) return ''
    const singleLine = note.replace(/[\r\n]+/g, ' ').trim()
    const clean = singleLine.replace(/[*#_`~[\]()]/g, '')
    if (clean.length > 35) {
      return clean.slice(0, 35) + '...'
    }
    return clean
  }

  // Функция для форматирования комбинированного названия устройства
  const getCombinedDeviceText = (item: ProjectInterfaceBinding): string => {
    const parts = []
    if (item.manufacturer) parts.push(item.manufacturer)
    if (item.specification_name) parts.push(item.specification_name)
    const details = parts.length > 0 ? ` (${parts.join(' ')})` : ''
    return `${item.device_name || ''}${details}`
  }

  const getWifiCombinedDeviceText = (client: WifiClient): string => {
    const parts = []
    if (client.manufacturer) parts.push(client.manufacturer)
    if (client.specification_name) parts.push(client.specification_name)
    const details = parts.length > 0 ? ` (${parts.join(' ')})` : ''
    return `${client.device_name || ''}${details}`
  }

  const handleMouseEnterNote = (item: ProjectInterfaceBinding | WifiClient, e: React.MouseEvent<HTMLSpanElement>) => {
    if (!item.note) return
    const rect = e.currentTarget.getBoundingClientRect()
    const tooltipWidth = 320
    let x = rect.left
    if (rect.left + tooltipWidth > window.innerWidth) {
      x = window.innerWidth - tooltipWidth - 16
    }
    const showAbove = rect.bottom + 150 > window.innerHeight
    setActiveTooltip({
      note: item.note,
      x: Math.max(16, x),
      y: showAbove ? rect.top - 8 : rect.bottom + 8,
      showAbove,
    })
  }

  const handleMouseLeaveNote = () => {
    setActiveTooltip(null)
  }

  const applyWrap = (prefix: string, suffix = prefix) => {
    const ta = document.getElementById('note-textarea') as HTMLTextAreaElement | null
    if (!ta) return
    const start = ta.selectionStart ?? 0
    const end = ta.selectionEnd ?? 0
    const selected = editingNoteValue.slice(start, end)
    const nextValue = editingNoteValue.slice(0, start) + prefix + selected + suffix + editingNoteValue.slice(end)
    setEditingNoteValue(nextValue)
    requestAnimationFrame(() => {
      ta.focus()
      ta.selectionStart = start + prefix.length
      ta.selectionEnd = start + prefix.length + selected.length
    })
  }

  const insertAtCursor = (text: string) => {
    const ta = document.getElementById('note-textarea') as HTMLTextAreaElement | null
    if (!ta) return
    const start = ta.selectionStart ?? 0
    const end = ta.selectionEnd ?? 0
    const nextValue = editingNoteValue.slice(0, start) + text + editingNoteValue.slice(end)
    setEditingNoteValue(nextValue)
    requestAnimationFrame(() => {
      ta.focus()
      ta.selectionStart = start + text.length
      ta.selectionEnd = start + text.length
    })
  }

  const handleSaveNote = () => {
    if (!editingNoteItem) return
    const note = editingNoteValue.trim() === '' ? null : editingNoteValue.trim()

    if ('client_name' in editingNoteItem) {
      updateWifiClientMutation.mutate({
        id: editingNoteItem.id,
        data: {
          client_name: editingNoteItem.client_name,
          device_instance_id: editingNoteItem.device_instance_id,
          ip_last_octet: editingNoteItem.ip_last_octet,
          mac_address: editingNoteItem.mac_address,
          note
        }
      })
    } else {
      saveMutation.mutate({
        device_instance_id: editingNoteItem.device_instance_id,
        interface_id: editingNoteItem.interface_id,
        network_id: editingNoteItem.network_id,
        ip_last_octet: editingNoteItem.ip_last_octet,
        mac_address: editingNoteItem.mac_address,
        note,
        slave_id: editingNoteItem.slave_id
      })
    }

    setEditingNoteItem(null)
  }

  // Хэндлер изменения сети
  const handleNetworkChange = (item: ProjectInterfaceBinding, newNetworkIdStr: string) => {
    const key = `${item.device_instance_id}_${item.interface_id}`
    const networkId = newNetworkIdStr ? Number(newNetworkIdStr) : null

    let ip_last_octet: number | null = null
    let mac_address: string | null = null
    let note: string | null = item.note
    let slave_id: number | null = null

    if (networkId !== null) {
      const targetNetwork = networks.find(n => n.id === networkId)
      if (targetNetwork) {
        if (targetNetwork.network_type === 'lan') {
          const rawVal = localValues[key] !== undefined ? localValues[key] : (item.ip_last_octet ?? '')
          ip_last_octet = rawVal !== '' ? Number(rawVal) : null
          
          mac_address = localMacs[key] !== undefined ? localMacs[key] : (item.mac_address ?? null)
        } else if (targetNetwork.network_type === 'rs485' || targetNetwork.network_type === 'rs485_modbus') {
          const rawVal = localValues[key] !== undefined ? localValues[key] : (item.slave_id ?? '')
          slave_id = rawVal !== '' ? Number(rawVal) : null
        }
      }
    }

    saveMutation.mutate({
      device_instance_id: item.device_instance_id,
      interface_id: item.interface_id,
      network_id: networkId,
      ip_last_octet,
      mac_address,
      note,
      slave_id
    })
  }

  // Хэндлер изменения текстового поля в локальном буфере (для октета / slave_id)
  const handleInputChange = (item: ProjectInterfaceBinding, val: string) => {
    const key = `${item.device_instance_id}_${item.interface_id}`
    setLocalValues(prev => ({ ...prev, [key]: val }))
  }

  // Сохранение числового поля, MAC или примечания по Blur/Enter
  const handleInputBlur = (item: ProjectInterfaceBinding, fieldType: 'octet' | 'mac' | 'slave') => {
    const key = `${item.device_instance_id}_${item.interface_id}`
    const network = networks.find(n => n.id === item.network_id)
    if (!network) return

    let ip_last_octet = item.ip_last_octet
    let mac_address = item.mac_address
    let note = item.note
    let slave_id = item.slave_id
    let hasChanged = false

    if (fieldType === 'octet' && network.network_type === 'lan') {
      const rawVal = localValues[key]
      if (rawVal !== undefined) {
        const parsed = rawVal === '' ? null : Number(rawVal)
        if (parsed !== item.ip_last_octet) {
          ip_last_octet = parsed
          hasChanged = true
        }
      }
    } else if (fieldType === 'mac' && network.network_type === 'lan') {
      const rawVal = localMacs[key]
      if (rawVal !== undefined) {
        const parsed = rawVal.trim() === '' ? null : rawVal.trim()
        if (parsed !== item.mac_address) {
          mac_address = parsed
          hasChanged = true
        }
      }
    } else if (fieldType === 'slave' && (network.network_type === 'rs485' || network.network_type === 'rs485_modbus')) {
      const rawVal = localValues[key]
      if (rawVal !== undefined) {
        const parsed = rawVal === '' ? null : Number(rawVal)
        if (parsed !== item.slave_id) {
          slave_id = parsed
          hasChanged = true
        }
      }
    }

    if (hasChanged) {
      saveMutation.mutate({
        device_instance_id: item.device_instance_id,
        interface_id: item.interface_id,
        network_id: item.network_id,
        ip_last_octet,
        mac_address,
        note,
        slave_id
      })
    }
  }

  // Хэндлеры для WiFi-клиентов
  const handleOpenAddWifiModal = () => {
    setEditingWifiClient(null)
    setWifiClientName('')
    setWifiDeviceInstanceId('')
    setWifiIpLastOctet('')
    setWifiMacAddress('')
    setWifiNote('')
    setDeviceSearchQuery('')
    setIsWifiModalOpen(true)
  }

  const handleOpenEditWifiModal = (client: WifiClient) => {
    setEditingWifiClient(client)
    setWifiClientName(client.client_name)
    setWifiDeviceInstanceId(client.device_instance_id ? String(client.device_instance_id) : '')
    setWifiIpLastOctet(client.ip_last_octet !== null ? String(client.ip_last_octet) : '')
    setWifiMacAddress(client.mac_address || '')
    setWifiNote(client.note || '')
    setDeviceSearchQuery('')
    setIsWifiModalOpen(true)
  }

  const handleSaveWifiClient = (e: React.FormEvent) => {
    e.preventDefault()
    if (!networkId) return

    const ipOctetNum = wifiIpLastOctet.trim() === '' ? null : Number(wifiIpLastOctet)
    if (ipOctetNum !== null && (isNaN(ipOctetNum) || ipOctetNum < 0 || ipOctetNum > 255)) {
      showToast('IP октет должен быть числом от 0 до 255', 'error')
      return
    }

    const payload = {
      device_instance_id: wifiDeviceInstanceId ? Number(wifiDeviceInstanceId) : null,
      client_name: wifiClientName.trim(),
      ip_last_octet: ipOctetNum,
      mac_address: wifiMacAddress.trim() || null,
      note: wifiNote.trim() || null,
    }

    if (!payload.client_name) {
      showToast('Имя WiFi-клиента обязательно для заполнения', 'error')
      return
    }

    if (editingWifiClient) {
      updateWifiClientMutation.mutate({
        id: editingWifiClient.id,
        data: payload
      })
    } else {
      createWifiClientMutation.mutate({
        network_id: Number(networkId),
        ...payload
      })
    }

    setIsWifiModalOpen(false)
  }

  const handleDeleteWifiClient = (client: WifiClient) => {
    if (window.confirm(`Вы уверены, что хотите удалить WiFi-клиента "${client.client_name}"?`)) {
      deleteWifiClientMutation.mutate(client.id)
    }
  }

  const isPageLoading = networksLoading || interfacesLoading || (currentNetwork?.network_type === 'lan' && wifiClientsLoading)


  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Link to="/projects" className="hover:text-blue-600">Проекты</Link>
          <span>/</span>
          <Link to={`/projects/${projectId}`} className="hover:text-blue-600">{project?.name || 'Проект'}</Link>
          <span>/</span>
          <Link to={`/projects/${projectId}/networks`} className="hover:text-blue-600 font-medium">Сети проекта</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">
            Интерфейсы сети «{currentNetwork?.name || '...'}»
          </span>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Сетевые интерфейсы сети «{currentNetwork?.name || 'Загрузка...'}»
            </h1>
            <p className="text-gray-600 mt-1">
              Назначение физических портов оборудования к логической сети и распределение IP-адресов / MAC / Slave ID
            </p>
          </div>
          {currentNetwork && (
            <Link
              to={`/projects/${projectId}/networks/${networkId}/print`}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
            >
              🖨️ Версия для печати
            </Link>
          )}
        </div>
      </div>

      {/* Список интерфейсов */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
        {isPageLoading ? (
          <div className="text-center py-12 text-gray-500 font-medium">Загрузка данных интерфейсов...</div>
        ) : filteredInterfaces.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Маркировка
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Устройство (Спецификация)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Физический порт
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Привязанная сеть
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Параметры адресации
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Примечание
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInterfaces.map((item) => {
                  const key = `${item.device_instance_id}_${item.interface_id}`
                  const filteredNetworks = getFilteredNetworks(item.interface_type_code)
                  
                  // Получаем текущую привязанную сеть
                  const currentBoundNetwork = networks.find(n => n.id === item.network_id)
                  const netType = currentBoundNetwork?.network_type

                  // Локальные значения для инпутов
                  const inputValue = localValues[key] !== undefined 
                    ? localValues[key] 
                    : (netType === 'lan' ? (item.ip_last_octet ?? '') : (item.slave_id ?? ''))

                  const macValue = localMacs[key] !== undefined
                    ? localMacs[key]
                    : (item.mac_address ?? '')

                  const isDuplicate = item.network_id === Number(networkId) && (
                    netType === 'lan' 
                      ? duplicateStatus.isDuplicate(item.ip_last_octet)
                      : (['rs485', 'rs485_modbus'].includes(netType ?? '') ? duplicateStatus.isDuplicate(item.slave_id) : false)
                  )

                  return (
                    <tr 
                      key={key} 
                      className={`transition-colors duration-150 ${
                        isDuplicate 
                          ? 'bg-red-50 hover:bg-red-100 text-red-950 font-semibold' 
                          : 'hover:bg-gray-50'
                      }`}
                      title={isDuplicate ? 'Дубликат' : undefined}
                    >
                      {/* Маркировка */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold">
                        <Link
                          to={`/projects/${projectId}/panels/${item.electrical_panel_id}/device-instances/${item.device_instance_id}`}
                          className="text-blue-900 hover:text-blue-600 hover:underline"
                        >
                          {item.device_position_label}
                        </Link>
                      </td>

                      {/* Устройство (Спецификация) */}
                      <td className="px-4 py-4 text-sm">
                        {item.specification_id ? (
                          <Link
                            to={`/projects/${projectId}/devices/${item.specification_id}`}
                            className="text-gray-900 hover:text-blue-600 hover:underline font-medium"
                          >
                            {getCombinedDeviceText(item)}
                          </Link>
                        ) : (
                          <span className="text-gray-900 font-medium">{item.device_name}</span>
                        )}
                      </td>

                      {/* Порт */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-bold flex items-center gap-1">
                          🔌 {item.interface_chassis_label}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                          {item.interface_type_name} ({item.interface_type_code})
                        </div>
                      </td>

                      {/* Сеть */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {canEdit ? (
                          <select
                            value={item.network_id ?? ''}
                            onChange={(e) => handleNetworkChange(item, e.target.value)}
                            className="block w-full max-w-[200px] rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs px-2.5 py-1.5 border bg-white"
                          >
                            <option value="">-- Не выбрана --</option>
                            {filteredNetworks.map(net => (
                              <option key={net.id} value={net.id}>
                                {net.name} ({net.code})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-600 font-medium">
                            {item.network_name ? `${item.network_name} (${item.network_code})` : <span className="text-gray-400 italic">Не выбрана</span>}
                          </span>
                        )}
                      </td>

                      {/* Адресные параметры */}
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {item.network_id ? (
                          <div className="space-y-1.5">
                            {netType === 'lan' && (
                              <div className="flex flex-col gap-2">
                                {/* Октет IP */}
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[11px] text-gray-500 w-24">IP октет:</span>
                                    <input
                                      type="number"
                                      min="0"
                                      max="255"
                                      disabled={!canEdit}
                                      value={inputValue}
                                      onChange={(e) => handleInputChange(item, e.target.value)}
                                      onBlur={() => handleInputBlur(item, 'octet')}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.currentTarget.blur()
                                      }}
                                      placeholder="123"
                                      className={`w-16 rounded border px-1.5 py-0.5 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none ${
                                        isDuplicate 
                                          ? 'border-red-300 bg-red-100/50 text-red-900 focus:border-red-500 focus:ring-red-500 font-bold' 
                                          : 'border-gray-300 bg-white'
                                      }`}
                                    />
                                  </div>
                                  
                                  {inputValue !== '' && (
                                    <span className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-semibold px-1.5 py-0.5 rounded font-mono">
                                      IP: {calculateFullIp(currentBoundNetwork?.network_config?.subnet, inputValue)}
                                    </span>
                                  )}
                                </div>

                                {/* MAC-адрес */}
                                <div className="flex items-center gap-1">
                                  <span className="text-[11px] text-gray-500 w-24">MAC-адрес:</span>
                                  <input
                                    type="text"
                                    disabled={!canEdit}
                                    value={macValue}
                                    onChange={(e) => setLocalMacs(prev => ({ ...prev, [key]: e.target.value }))}
                                    onBlur={() => handleInputBlur(item, 'mac')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.currentTarget.blur()
                                    }}
                                    placeholder="AA:BB:CC:DD:EE:FF"
                                    className="w-36 rounded border border-gray-300 px-1.5 py-0.5 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none font-mono"
                                  />
                                </div>
                              </div>
                            )}

                            {(netType === 'rs485' || netType === 'rs485_modbus') && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 font-medium">Slave ID:</span>
                                <input
                                  type="number"
                                  min="1"
                                  max="247"
                                  disabled={!canEdit}
                                  value={inputValue}
                                  onChange={(e) => handleInputChange(item, e.target.value)}
                                  onBlur={() => handleInputBlur(item, 'slave')}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur()
                                  }}
                                  placeholder="1-247"
                                  className={`w-20 rounded border px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none ${
                                    isDuplicate 
                                      ? 'border-red-300 bg-red-100/50 text-red-900 focus:border-red-500 focus:ring-red-500 font-bold' 
                                      : 'border-gray-300 bg-white'
                                  }`}
                                />
                              </div>
                            )}

                            {!['lan', 'rs485', 'rs485_modbus'].includes(netType ?? '') && (
                              <span className="text-xs text-gray-400 italic">
                                Кастомная сеть (без параметров адресации)
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Сначала выберите сеть</span>
                        )}
                      </td>

                      {/* Примечание */}
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          {item.note ? (
                            <span
                              className="text-xs text-gray-600 cursor-help border-b border-dotted border-gray-400 max-w-[200px] truncate inline-block"
                              onMouseEnter={(e) => handleMouseEnterNote(item, e)}
                              onMouseLeave={handleMouseLeaveNote}
                            >
                              {getNotePreview(item.note)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">—</span>
                          )}
                          
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingNoteItem(item)
                                setEditingNoteValue(item.note ?? '')
                                setActiveTab('write')
                              }}
                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded transition-colors"
                              title={item.note ? "Редактировать примечание" : "Добавить примечание"}
                            >
                              {item.note ? (
                                <Edit2 className="w-3.5 h-3.5" />
                              ) : (
                                <Plus className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>

                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16 text-gray-500">
            <div className="text-5xl mb-3">🔌</div>
            <div className="text-lg font-medium">Нет доступных портов этого типа на схемах сетей</div>
            <p className="text-sm text-gray-400 mt-1.5 max-w-md mx-auto">
              Чтобы здесь появились интерфейсы, добавьте устройства с сетевыми портами совместимого типа на любую схему с типом «Сеть проекта» (network).
            </p>
          </div>
        )}
      </div>

      {/* Беспроводные (WiFi) клиенты сети */}
      {currentNetwork?.network_type === 'lan' && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mt-6">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Wifi className="w-5 h-5 text-blue-600 animate-pulse" />
                Беспроводные (WiFi) клиенты сети
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Устройства, подключенные к беспроводной сети (не требующие физического кабеля на схемах)
              </p>
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={handleOpenAddWifiModal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Добавить WiFi-клиента
              </button>
            )}
          </div>

          {wifiClients.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Прибор проекта
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Имя WiFi-клиента
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      IP адрес
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      MAC-адрес
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Примечание
                    </th>
                    {canEdit && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Действия
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {wifiClients.map((client) => {
                    const fullIp = calculateFullIp(currentNetwork?.network_config?.subnet, client.ip_last_octet)
                    const isDuplicate = duplicateStatus.isDuplicate(client.ip_last_octet)
                    return (
                      <tr 
                        key={client.id} 
                        className={`transition-colors duration-150 ${
                          isDuplicate 
                            ? 'bg-red-50 hover:bg-red-100 text-red-950 font-semibold' 
                            : 'hover:bg-gray-50'
                        }`}
                        title={isDuplicate ? 'Дубликат' : undefined}
                      >
                        {/* Прибор проекта */}
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          {client.device_instance_id ? (
                            <Link
                              to={`/projects/${projectId}/panels/${client.electrical_panel_id}/device-instances/${client.device_instance_id}`}
                              className="text-blue-900 hover:text-blue-600 hover:underline font-bold"
                            >
                              {client.device_position_label ? `${client.device_position_label} ` : ''}
                              <span className="font-normal text-gray-500">
                                {getWifiCombinedDeviceText(client)}
                              </span>
                            </Link>
                          ) : (
                            <span className="text-gray-400 italic text-xs">Без привязаного прибора</span>
                          )}
                        </td>

                        {/* Имя WiFi-клиента */}
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {client.client_name}
                        </td>

                        {/* IP адрес */}
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                          {client.ip_last_octet !== null ? (
                            <div className="flex items-center gap-1.5">
                              <span className={`font-bold ${isDuplicate ? 'text-red-700' : 'text-gray-800'}`}>{fullIp}</span>
                              <span className="text-[10px] text-gray-400 font-normal">(.{client.ip_last_octet})</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-xs font-sans">Не назначен</span>
                          )}
                        </td>

                        {/* MAC-адрес */}
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                          {client.mac_address || <span className="text-gray-400 italic text-xs font-sans">—</span>}
                        </td>

                        {/* Примечание */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            {client.note ? (
                              <span
                                className="text-xs text-gray-600 cursor-help border-b border-dotted border-gray-400 max-w-[200px] truncate inline-block"
                                onMouseEnter={(e) => handleMouseEnterNote(client, e)}
                                onMouseLeave={handleMouseLeaveNote}
                              >
                                {getNotePreview(client.note)}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic text-xs">—</span>
                            )}
                            
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingNoteItem(client)
                                  setEditingNoteValue(client.note ?? '')
                                  setActiveTab('write')
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded transition-colors"
                                title={client.note ? "Редактировать примечание" : "Добавить примечание"}
                              >
                                {client.note ? (
                                  <Edit2 className="w-3.5 h-3.5" />
                                ) : (
                                  <Plus className="w-3.5 h-3.5" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>

                        {/* Действия */}
                        {canEdit && (
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleOpenEditWifiModal(client)}
                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded transition-colors"
                                title="Редактировать"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteWifiClient(client)}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded transition-colors"
                                title="Удалить"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-2">📡</div>
              <div className="text-base font-semibold">Нет зарегистрированных беспроводных клиентов</div>
              <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                Здесь можно зарегистрировать беспроводные устройства (колонки, пылесосы, смартфоны), которые не отображаются на физических кабельных схемах.
              </p>
              {canEdit && (
                <button
                  type="button"
                  onClick={handleOpenAddWifiModal}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Зарегистрировать первое устройство
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Модалка редактирования примечания */}
      {editingNoteItem && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Редактирование примечания</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {'client_name' in editingNoteItem ? (
                    <>
                      WiFi клиент: <span className="font-semibold text-gray-700">{editingNoteItem.client_name}</span>
                      {editingNoteItem.device_position_label && (
                        <> ({editingNoteItem.device_position_label})</>
                      )}
                    </>
                  ) : (
                    <>
                      Устройство: <span className="font-semibold text-gray-700">{editingNoteItem.device_position_label}</span> ({editingNoteItem.device_name}), порт: <span className="font-semibold text-gray-700">{editingNoteItem.interface_chassis_label}</span>
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingNoteItem(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs & Toolbar */}
            <div className="px-6 py-3 border-b border-gray-200 bg-white flex flex-wrap gap-4 items-center justify-between">
              <div className="flex bg-gray-100 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => setActiveTab('write')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    activeTab === 'write' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  Редактор
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('preview')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    activeTab === 'preview' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  Предпросмотр
                </button>
              </div>

              {activeTab === 'write' && (
                <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-lg border">
                  <button
                    type="button"
                    onClick={() => applyWrap('**')}
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
                    title="Жирный (Bold)"
                  >
                    <Bold className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyWrap('_')}
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
                    title="Курсив (Italic)"
                  >
                    <Italic className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyWrap('`', '`')}
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
                    title="Код (Inline Code)"
                  >
                    <Code className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-px h-4 bg-gray-300 mx-1"></span>
                  <button
                    type="button"
                    onClick={() => applyWrap('[', '](url)')}
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
                    title="Ссылка (Link)"
                  >
                    <Link2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertAtCursor('\n- ')}
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
                    title="Список (List)"
                  >
                    <List className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>

            {/* Content Area */}
            <div className="flex-1 p-6 overflow-y-auto min-h-[300px]">
              {activeTab === 'write' ? (
                <textarea
                  id="note-textarea"
                  value={editingNoteValue}
                  onChange={(e) => setEditingNoteValue(e.target.value)}
                  placeholder="Введите примечание. Поддерживается Markdown разметка (заголовки #, списки -, ссылки, жирный текст и др.)"
                  className="w-full h-full min-h-[250px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono text-sm resize-y"
                  autoFocus
                />
              ) : (
                <div className="w-full min-h-[250px] p-4 bg-gray-50 border border-gray-200 rounded-lg overflow-y-auto markdown-content">
                  {editingNoteValue.trim() ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {editingNoteValue}
                    </ReactMarkdown>
                  ) : (
                    <span className="text-gray-400 italic text-sm">Нечего предпросматривать. Напишите что-нибудь во вкладке «Редактор».</span>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingNoteItem(null)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSaveNote}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно добавления/редактирования WiFi-клиента */}
      {isWifiModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">
                {editingWifiClient ? 'Редактировать WiFi-клиента' : 'Добавить WiFi-клиента'}
              </h3>
              <button
                type="button"
                onClick={() => setIsWifiModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSaveWifiClient} className="p-6 space-y-4">
              {/* Прибор проекта */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Прибор проекта (Опционально)
                </label>
                
                {/* Быстрый поиск прибора */}
                <input
                  type="text"
                  value={deviceSearchQuery}
                  onChange={(e) => setDeviceSearchQuery(e.target.value)}
                  placeholder="🔍 Быстрый поиск по маркировке, модели..."
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs px-3 py-1.5 border bg-white mb-2"
                />

                <select
                  value={wifiDeviceInstanceId}
                  onChange={(e) => {
                    const devId = e.target.value
                    setWifiDeviceInstanceId(devId)
                    // Auto-fill client name if it's empty
                    if (devId && !wifiClientName) {
                      const selectedDev = projectDevices.find(d => d.id === Number(devId))
                      if (selectedDev) {
                        const name = selectedDev.position_label 
                          ? `${selectedDev.position_label} (${selectedDev.manufacturer || ''} ${selectedDev.model || selectedDev.spec_name || ''})`.trim()
                          : `${selectedDev.manufacturer || ''} ${selectedDev.model || selectedDev.spec_name || ''}`.trim()
                        setWifiClientName(name)
                      }
                    }
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2 border bg-white"
                >
                  <option value="">-- Без прибора (только имя клиента) --</option>
                  {filteredProjectDevices.map((dev) => (
                    <option key={dev.id} value={dev.id}>
                      {dev.position_label ? `[${dev.position_label}] ` : ''}
                      {dev.manufacturer ? `${dev.manufacturer} ` : ''}
                      {dev.model || dev.spec_name || 'Без названия'} (id: {dev.id})
                    </option>
                  ))}
                </select>
              </div>

              {/* Имя WiFi-клиента */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Имя WiFi-клиента <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={wifiClientName}
                  onChange={(e) => setWifiClientName(e.target.value)}
                  placeholder="Например: Колонка Яндекс Алиса, Смартфон"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2 border bg-white"
                />
              </div>

              {/* IP Последний октет */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  IP-адрес (последний октет)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="255"
                    value={wifiIpLastOctet}
                    onChange={(e) => setWifiIpLastOctet(e.target.value)}
                    placeholder="Например: 123"
                    className="block w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2 border bg-white"
                  />
                  {wifiIpLastOctet !== '' && (
                    <span className="text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded font-mono">
                      IP: {calculateFullIp(currentNetwork?.network_config?.subnet, wifiIpLastOctet)}
                    </span>
                  )}
                </div>
              </div>

              {/* MAC-адрес */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  MAC-адрес
                </label>
                <input
                  type="text"
                  value={wifiMacAddress}
                  onChange={(e) => setWifiMacAddress(e.target.value)}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2 border bg-white font-mono"
                />
              </div>

              {/* Примечание */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Примечание (Краткое описание)
                </label>
                <textarea
                  value={wifiNote}
                  onChange={(e) => setWifiNote(e.target.value)}
                  placeholder="Дополнительная информация об устройстве"
                  rows={3}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm px-3 py-2 border bg-white"
                />
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsWifiModalOpen(false)}
                  className="px-4 py-2 border rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                >
                  {editingWifiClient ? 'Сохранить изменения' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Всплывающий Tooltip для просмотра примечания */}
      {activeTooltip && (
        <div
          className={`fixed z-[9999] bg-slate-900 text-slate-100 p-3 rounded-lg shadow-xl border border-slate-700 max-w-sm w-80 text-xs pointer-events-none transition-opacity duration-150 ${
            activeTooltip.showAbove ? '-translate-y-full' : ''
          }`}
          style={{
            left: `${activeTooltip.x}px`,
            top: `${activeTooltip.y}px`,
          }}
        >
          <div className="markdown-content">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
              {activeTooltip.note}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  )
}
